import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Global React Error Boundary.
 * Catches any render-time error in child components and displays
 * a polite fallback instead of a blank white screen.
 *
 * Props:
 *   message  – optional custom message string
 *   fallback – optional fully custom fallback JSX node
 *   children – the subtree to protect
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Uncaught render error:', error);
    console.error('[ErrorBoundary] Component stack:', info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Allow fully custom fallback via prop
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex items-center justify-center min-h-[40vh] px-4 py-16">
          <div className="rounded-lg border bg-card p-10 text-center max-w-md w-full shadow-sm">
            <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-6 w-6 text-gold" />
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-2">
              Something went wrong
            </h3>
            <p className="text-sm text-muted-foreground font-body leading-relaxed mb-6">
              {this.props.message ||
                'An unexpected error occurred while loading this section. Please try again.'}
            </p>
            <button
              onClick={this.handleReset}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-gold text-[#1B2A4A] hover:bg-gold/90 px-6 py-2 text-sm font-body font-medium transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
